import { cloneDeep } from 'lodash'
import formatDate from '@utils/format-date-with-time'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { mapState } from 'vuex'
import userHasRoles from '@utils/userPermissions'
import { hideValues, updateColumns } from '@utils/helpers/columns'

export default {
  props: {
    filters: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      clearAllFilters: false,
      loading: false,
      content: [],
      rowsPerPage: [10, 25, 50],
      page: 1,
      totalPages: 0,
      sortBy: 'organisation',
      descending: true,
      first: false,
      last: false,
      pagination: {},
      totalElements: 0,
      paginationEventCount: 0,
      paginationIsReady: false,
      filtersForApi: {},
      otherParams: {},
      allowedRolesToSeeDetails: [
        'SUPERUSER',
        'DICTIONARIES_OPERATOR',
        'DOCUMENTS_OPERATOR',
        'GUEST',
      ],
    }
  },
  watch: {
    filters(value) {
      if (!this.filtersEmpty(value)) {
        this.getDataSet()
        return
      }
      if (this.clearAllFilters) {
        this.getDataSet()
      }
    },
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.modelName,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      unitsMeasurementValue: state => state.userSettings.userSettings['unitsMeasurement'],
    }),
    unitsMeasurement() {
      return this.$store.getters['userSettings/getUnitsMeasurement']
    },
    userAllowedToNavigate() {
      return userHasRoles(
        this.$store.state.auth.userRole,
        this.allowedRolesToSeeDetails
      )
    },
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filtersForApi,
        ...this.otherParams,
      }
    },
  },
  methods: {
    init() {
      this.getPaginator()
      this.getDataSet()
    },
    prepareFilterParams() {
      let filtersForApi = {}
      let adoptedFilters = this.filters
        ? { ...this.filters.common, ...this.filters.custom }
        : {}
      for (const key of Object.keys(adoptedFilters)) {
        if (adoptedFilters[key]) {
          switch (key) {
            case 'postcodeFrom': {
              filtersForApi['filter_postcode_from_value'] = adoptedFilters[key]
              break
            }
            case 'postcodeTo': {
              filtersForApi['filter_postcode_to_value'] = adoptedFilters[key]
              break
            }
            case 'consumption':
            case 'currentVolume': {
              if (adoptedFilters[key].value) {
                filtersForApi[`filter_${key}_value`] =
                  adoptedFilters[key].value *
                  this.unitsMeasurement.volumeMultiplier
                filtersForApi[`filter_${key}_comparison`] = this.getComparison(
                  adoptedFilters[key].comparison
                )
              }
              break
            }
            case 'securityStock': {
              if (adoptedFilters[key].value) {
                filtersForApi[`filter_${key}_value`] = adoptedFilters[key].value
                filtersForApi[`filter_${key}_comparison`] = this.getComparison(
                  adoptedFilters[key].comparison
                )
              }
              break
            }
            case 'oil': {
              filtersForApi['filter_oil_value'] = adoptedFilters[key]
              filtersForApi[`filter_oil_comparison`] = 'EQ'
              break
            }
            case 'serial': {
              if (
                this.modelName === 'ToppingUpOil' ||
                this.modelName === 'UnresponsiveTransmittersAndPlugs' ||
                this.modelName === 'ShipmentMistakes' ||
                this.modelName === 'MergedButNotInitializedPlugs'
              ) {
                filtersForApi[`filter_serial_value`] = adoptedFilters[key]
                filtersForApi[`filter_serial_comparison`] = 'EQ'
                break
              }
              filtersForApi[`filter_plug_value`] = adoptedFilters[key]
              filtersForApi[`filter_plug_comparison`] = 'EQ'
              break
            }
            default:
              filtersForApi[`filter_${key}_value`] = adoptedFilters[key]
              filtersForApi[`filter_${key}_comparison`] = 'EQ'
          }
        }
      }
      this.filtersForApi = filtersForApi
    },
    getFilters() {
      return this.$store.getters['filters/getFilters'](this.modelName)
    },
    filtersEmpty(filters) {
      let commonFiltersEmpty = true
      let customFiltersEmpty = true
      if (filters) {
        for (const key of Object.keys(filters.common)) {
          if (filters.common[key] && filters.common[key].length !== 0) {
            commonFiltersEmpty = false
            break
          }
        }
        for (const key of Object.keys(filters.custom)) {
          if (filters.custom[key].value !== null) {
            customFiltersEmpty = false
            break
          }
        }
      }
      return commonFiltersEmpty && customFiltersEmpty
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || false
        this.pagination.page = 1
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
      this.paginationIsReady = true
    },
    changePage(value) {
      this.page = value
      this.getDataSet()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.getDataSet()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.descending ? 'desc' : 'asc',
        header.value === this.sortBy ? 'active' : '',
      ]
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.getDataSet()
    },
    setResponseParams(response) {
      this.totalPages = response.totalPages
      this.first = response.first
      this.last = response.last
      this.page = response.number + 1
      this.pagination = {
        ...this.pagination,
        page: 1,
        rowsPerPage: response.size,
        // sortBy: 'oil',
        // descending: this.descending,
      }
    },
    resetPage() {
      if (this.paginationIsReady) {
        this.page = 1
        this.pagination.page = 1
        if (!this.extParams) {
          this.persistPagination()
        }
      }
    },
    clearFilters() {
      this.clearAllFilters = true
    },
    persistPagination() {
      this.$store.commit('pagination/SET', {
        name: this.modelName,
        pagination: cloneDeep(this.pagination),
      })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        Number(value)
      )
    },
    convertValue(value) {
      if (value) {
        return (value / this.unitsMeasurement.volumeMultiplier).toFixed(1)
      }
      return '0.0'
    },
    dateFormat(date) {
      const formatedDate = formatDate(date)
      return formatedDate === 'Invalid Date' ? '-' : formatedDate
    },
    getComparison(comparison) {
      switch (comparison) {
        case 'equal':
          return 'EQ'
        case 'more':
          return 'GT'
        case 'less':
          return 'LT'
        case 'notEqual':
          return 'NE'
        default:
          return 'EQ'
      }
    },
    convertTemperature(value) {
      if (this.unitsMeasurementValue !== 'us') {
        return `${value} ℃`
      } else {
        const fahrenheit = value * (9 / 5) + 32
        return `${fahrenheit.toFixed(1)} ℉`
      }
    },
    export() {
      if (!this.content || !this.content.length) {
        return
      }
      let csv = ''
      // Generate first row
      const firstRow = this.headers.map(elem => this.$t(`label__${elem.text}`))
      csv += `${firstRow.join(';')}\n`
      const columns = this.headers.map(elem => elem.value)
      // Generate data rows
      this.content.forEach(row => {
        const tmp = []
        columns.forEach(column => {
          let value = null
          switch (column) {
            case 'organisation':
            case 'plugOrg':
            case 'transmitterOrg':
              value = this.getValue('organization', row[column])
              break
            case 'oil':
              value = this.getValue('goods', row[column])
              break
            case 'date':
            case 'measureTime':
            case 'time':
              value = this.dateFormat(row[column])
              break
            case 'avgAmount':
            case 'lastAmount':
            case 'amount':
            case 'initialAmount':
            case 'currentAmount':
            case 'currentVolume':
            case 'averageDailyOilUsageDuringLastMonth':
            case 'averageDailyOilUsageDuringLastWeek':
            case 'sufficientForDays':
            case 'prevAmount':
            case 'openedAmount':
            case 'drumVolume':
            case 'difference':
              value = (
                row[column] / this.unitsMeasurement.volumeMultiplier
              ).toFixed(1)
              break
            case 'delta':
              value = (
                row[column] / this.unitsMeasurement.volumeMultiplier
              ).toFixed(1)
              break
            default:
              value = row[column]
              break
          }
          tmp.push(value)
        })
        csv += `${tmp.join(';')}\n`
      })
      this.download(csv)
    },
    download(csv) {
      const name = this.$t(`reports__${this.$route.params.name}`)
      const date = moment().format('YYYY-MM-DD_HH-mm-ss')
      const file = new File([csv], `${name}_${date}.csv`, {
        type: 'text/plain;charset=utf-8',
      })
      saveAs(file)
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
  },
}
